import React, { useState } from 'react';
import { Form, Input, Button, Card, Select, InputNumber, Row, Col, Typography, Space, message } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Station {
  stationCode: string;
  stationName: string;
  stationNameEn?: string;
  operationDesc?: string;
  stationOrder: number;
  ctTime: number;
  stLabor: number;
  burdenBase: number;
}

const IEDataEntry: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [stations, setStations] = useState<Station[]>([
    { stationCode: 'ST01', stationName: '', stationOrder: 1, ctTime: 0, stLabor: 1, burdenBase: 0 }
  ]);
  const [loading, setLoading] = useState(false);

  const addStation = () => {
    setStations([...stations, {
      stationCode: `ST${String(stations.length + 1).padStart(2, '0')}`,
      stationName: '',
      stationOrder: stations.length + 1,
      ctTime: 0,
      stLabor: 1,
      burdenBase: 0
    }]);
  };

  const removeStation = (index: number) => {
    if (stations.length > 1) {
      const newStations = stations.filter((_, i) => i !== index);
      setStations(newStations);
    }
  };

  const updateStation = (index: number, field: keyof Station, value: any) => {
    const newStations = [...stations];
    newStations[index] = { ...newStations[index], [field]: value };
    setStations(newStations);
  };

  const handleCreateProject = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Create project
      const projectRes: any = await api.post('/projects', {
        projectNo: values.projectNo,
        casNo: values.casNo,
        name: values.name,
        nameEn: values.nameEn,
        productType: values.productType,
        solutionType: values.solutionType,
        phase: values.phase,
        location: values.location
      });
      
      const newProjectId = projectRes.data.projectId;
      
      // Create route
      const routeRes: any = await api.post('/ie/routes', {
        projectId: newProjectId,
        routeName: values.routeName || 'Main Route',
        routeNameEn: values.routeNameEn || 'Main Route'
      });
      
      const newRouteId = routeRes.data.routeId;
      
      // Create stations
      await api.post('/ie/data/batch', {
        routeId: newRouteId,
        stations: stations
      });
      
      message.success(t('common.success'));
      
      // Navigate to cost calculation
      navigate(`/cost?projectId=${newProjectId}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={4}>{t('menu.ieData')}</Title>
      
      <Row gutter={24}>
        {/* Project Info */}
        <Col xs={24} lg={10}>
          <Card title="Project Information" style={{ marginBottom: 16 }}>
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="projectNo"
                    label={t('project.projectNo')}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="PRJ-2024-001" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="casNo" label={t('project.casNo')}>
                    <Input placeholder="CAS-001" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label={t('project.name')}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Project Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="nameEn" label={t('project.nameEn')}>
                    <Input placeholder="Project Name (English)" />
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="productType" label={t('project.productType')}>
                    <Select placeholder="Select">
                      <Option value="connector">Connector</Option>
                      <Option value="cable">Cable Assembly</Option>
                      <Option value="antenna">Antenna</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="solutionType" label={t('project.solutionType')} initialValue="semi">
                    <Select>
                      <Option value="auto">{t('project.auto')}</Option>
                      <Option value="semi">{t('project.semi')}</Option>
                      <Option value="manual">{t('project.manual')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="phase" label={t('project.phase')} initialValue="npi">
                    <Select>
                      <Option value="npi">{t('project.npi')}</Option>
                      <Option value="mass">{t('project.mass')}</Option>
                      <Option value="eol">{t('project.eol')}</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="location" label={t('project.location')}>
                    <Select placeholder="Select">
                      <Option value="Thailand">Thailand</Option>
                      <Option value="Mexico">Mexico</Option>
                      <Option value="China-Haiyan">China - Haiyan</Option>
                      <Option value="China-Shanghai">China - Shanghai</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item name="routeName" label={t('ie.route')}>
                <Input placeholder="Main Route" />
              </Form.Item>
            </Form>
          </Card>
        </Col>
        
        {/* Stations */}
        <Col xs={24} lg={14}>
          <Card 
            title={
              <Space>
                <span>{t('ie.station')}</span>
                <Button type="primary" icon={<PlusOutlined />} onClick={addStation} size="small">
                  {t('ie.addStation')}
                </Button>
              </Space>
            }
          >
            {stations.map((station, index) => (
              <Card 
                key={index} 
                size="small" 
                style={{ marginBottom: 16 }}
                title={`${t('ie.station')} ${index + 1}`}
                extra={
                  stations.length > 1 && (
                    <Button 
                      type="text" 
                      danger 
                      icon={<DeleteOutlined />}
                      onClick={() => removeStation(index)}
                    />
                  )
                }
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item label={t('ie.stationCode')}>
                      <Input 
                        value={station.stationCode}
                        onChange={(e) => updateStation(index, 'stationCode', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={18}>
                    <Form.Item label={t('ie.stationName')}>
                      <Input 
                        value={station.stationName}
                        onChange={(e) => updateStation(index, 'stationName', e.target.value)}
                        placeholder="Station Name"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label={t('ie.ctTime')}>
                      <InputNumber 
                        value={station.ctTime}
                        onChange={(v) => updateStation(index, 'ctTime', v || 0)}
                        min={0}
                        step={0.1}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t('ie.stLabor')}>
                      <InputNumber 
                        value={station.stLabor}
                        onChange={(v) => updateStation(index, 'stLabor', v || 0)}
                        min={0}
                        step={0.5}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label={t('ie.burdenBase')}>
                      <InputNumber 
                        value={station.burdenBase}
                        onChange={(v) => updateStation(index, 'burdenBase', v || 0)}
                        min={0}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Form.Item label={t('ie.operationDesc')}>
                  <TextArea 
                    value={station.operationDesc}
                    onChange={(e) => updateStation(index, 'operationDesc', e.target.value)}
                    rows={2}
                    placeholder="Operation description"
                  />
                </Form.Item>
              </Card>
            ))}
            
            <Space>
              <Button 
                type="primary" 
                icon={<SaveOutlined />}
                onClick={handleCreateProject}
                loading={loading}
              >
                {t('common.save')} & {t('cost.calculate')}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default IEDataEntry;